import React from "react";
import clsx from "clsx";
import { Mail, Smartphone } from "lucide-react";

import { kycLinkStyles as styles } from "./styles";
import type { CalloutTone } from "./copy";

export const formatPhone = (phone?: string) => {
  if (!phone) return "—";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) {
    return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  }
  if (digits.length === 12 && digits.startsWith("91")) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }
  return phone.startsWith("+") ? phone : `+91 ${phone}`;
};

export const RecipientsBlock = ({
  label,
  phone,
  email,
}: {
  label: string;
  phone?: string;
  email?: string;
}) => (
  <div className={styles.recipients}>
    <p className={styles.recipientsLabel}>{label}</p>
    <div className={styles.contactGrid}>
      <div className={styles.contactCol}>
        <span className={styles.contactMeta}>
          <Smartphone className="h-3.5 w-3.5 text-primary" />
          Phone
        </span>
        <p className={styles.contactValue}>{formatPhone(phone)}</p>
      </div>
      <div className={styles.contactCol}>
        <span className={styles.contactMeta}>
          <Mail className="h-3.5 w-3.5 text-primary" />
          Email
        </span>
        <p className={styles.contactValue}>{email || "—"}</p>
      </div>
    </div>
  </div>
);

export const Callout = ({
  tone,
  title,
  body,
  children,
}: {
  tone: CalloutTone;
  title: string;
  body?: string;
  children?: React.ReactNode;
}) => {
  const bar =
    tone === "success" ? "bg-[#16A34A]" : tone === "info" ? "bg-[#1D4ED8]" : "bg-[#C25912]";
  const titleColor =
    tone === "success" ? "text-[#16A34A]" : tone === "info" ? "text-[#1D4ED8]" : "text-[#C25912]";
  const bodyColor =
    tone === "success"
      ? "text-[#0F1729]"
      : tone === "info"
        ? "text-[#1D4ED8] opacity-85"
        : "text-[#C25912]";
  const bg =
    tone === "success"
      ? styles.calloutSuccess
      : tone === "info"
        ? styles.calloutInfo
        : styles.calloutWarn;

  return (
    <div className={clsx(styles.callout, bg)}>
      <div className={clsx(styles.calloutBar, bar)} />
      <div className={styles.calloutCopy}>
        <p className={clsx(styles.calloutTitle, titleColor)}>{title}</p>
        {body ? <p className={clsx(styles.calloutBody, bodyColor)}>{body}</p> : null}
        {children}
      </div>
    </div>
  );
};

export const PrimaryButton = ({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <button type="button" disabled={disabled} onClick={onClick} className={styles.primaryButton}>
    {children}
  </button>
);
